import { useEffect, useRef, type ReactNode } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { baseApi, TAGS } from '../api/baseApi'
import { useHeroQuery } from '../api/endpoints/hero'
import { useWorldQuery } from '../api/endpoints/world'
import { useStoryQuery } from '../api/endpoints/quests'
import { useActiveBattleQuery } from '../api/endpoints/battles'
import { useAppDispatch, useAppSelector } from '../app/store'
import { ui } from '../app/uiSlice'
import { useRefetchAt } from '../lib/time'
import { ChatSlot } from './ChatSlot'
import { Hud } from './Hud'
import { QuestSlot } from './QuestSlot'
import { ShellProvider, useShell, useSlotRef, useSystemMessage } from './ShellContext'
import { SystemStrip } from './SystemStrip'
import { useWorldPresence } from './useWorldPresence'
import './shell.css'

const BACKDROP = { wide: '/assets/ui/backdrop-wide.webp', narrow: '/assets/ui/backdrop-narrow.webp' }

/** The picture behind everything: the scene of the current place, else the
 *  picture of the hero's cell, else the backdrop. */
function Scene() {
  const { data: world } = useWorldQuery()
  const own = world?.place ? { wide: world.place.scene, narrow: world.place.scene_narrow } : { wide: world?.cell_scene ?? '', narrow: world?.cell_scene_narrow ?? '' }
  const wide = own.wide || BACKDROP.wide
  const narrow = own.narrow || own.wide || BACKDROP.narrow
  return (
    <div className="scene" aria-hidden="true">
      <picture>
        <source media="(max-width: 599px)" srcSet={narrow} />
        <img src={wide} alt="" />
      </picture>
    </div>
  )
}

function Notices() {
  const notices = useAppSelector(s => s.ui.notices)
  const dispatch = useAppDispatch()
  const first = notices[0]
  useEffect(() => {
    if (!first) return
    const t = setTimeout(() => dispatch(ui.actions.dropNotice(first.id)), 8000)
    return () => clearTimeout(t)
  }, [dispatch, first])
  useSystemMessage(first ? { kind: 'transition', text: first.text } : null)
  return null
}

/** Opens a battle once when it appears (an accepted encounter, a filled arena request). */
function useFollowBattle() {
  const { data } = useActiveBattleQuery(undefined, { pollingInterval: 0 })
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const followed = useRef<string | null>(null)
  const id = data?.battle?.id ?? null
  useEffect(() => {
    if (!id || followed.current === id) return
    followed.current = id
    if (!pathname.startsWith(`/play/battle/${id}`)) navigate(`/play/battle/${id}`, { state: { from: pathname } })
  }, [id, navigate, pathname])
  return id
}

function Layout({ children }: { children: ReactNode }) {
  const shell = useShell()
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()
  const hero = useHeroQuery()
  const story = useStoryQuery()
  const connection = useAppSelector(s => s.ui.connection)
  const lessMotion = useAppSelector(s => s.ui.lessMotion)
  const battleId = useFollowBattle()
  useWorldPresence(battleId)
  // Resources are shown as the server gave them; at the earliest «full at» the snapshot is re-read.
  const nextFull = hero.data?.resources.map(r => r.full_at).filter((x): x is string => Boolean(x)).sort()[0]
  useRefetchAt(nextFull, hero.refetch)
  useEffect(() => {
    document.documentElement.dataset.lessMotion = String(lessMotion)
  }, [lessMotion])

  const retryAll = () => dispatch(baseApi.util.invalidateTags([...TAGS]))
  useSystemMessage(connection === 'lost' ? { kind: 'error', text: 'Связь потеряна. Попробуйте ещё раз', onRetry: retryAll } : null)
  useSystemMessage(hero.isError && hero.data ? { kind: 'error', text: 'Данные героя не обновились', onRetry: () => void hero.refetch() } : null)
  const inBattle = battleId && !pathname.startsWith('/play/battle/')
  useSystemMessage(inBattle ? { kind: 'transition', text: 'Идёт бой', detail: 'вернуться', onRetry: undefined } : null)

  const mode = shell.mode ?? 'default'
  const combat = mode === 'combat'
  const sceneRef = useSlotRef('scene')
  const windowRef = useSlotRef('window')
  const hudRef = useSlotRef('hud')
  const claim = story.data?.quests.some(q => q.status === 'completed')

  return (
    <div className={`g3 shell shell--${mode} shell--layout-${shell.layout}${shell.system ? ' has-system' : ''}`} data-mode={mode}>
      <a className="shell__skip" href="#shell-main">К содержимому</a>
      <Scene />
      <div className="shell__scene-slot" ref={sceneRef} />

      <Link className="crest plank" to="/play" aria-label="Межа — к локации">
        <span className="plank__in"><img src="/assets/brand/mark.webp" alt="" /><b>МЕЖА</b></span>
      </Link>

      {!combat && mode !== 'dialog' && <QuestSlot story={story.data} loading={story.isLoading} failed={story.isError} line={mode !== 'default'} />}

      <main className="shell__stage" id="shell-main" tabIndex={-1}>
        {children}
        <div className="shell__window-slot" ref={windowRef} />
      </main>

      <ChatSlot compact={combat} />
      <SystemStrip message={shell.system} />
      {inBattle && <Link className="battle-return plank plank--gold" to={`/play/battle/${battleId}`}><span className="plank__in">К бою</span></Link>}
      <Notices />

      <div className="shell__hud-slot" ref={hudRef} />
      {!combat && (
        <Hud hero={hero.data} heroState={hero.isError ? 'error' : hero.data ? 'ready' : 'loading'} onRetryHero={() => void hero.refetch()}
          journalBadge={claim ? '!' : undefined} />
      )}
    </div>
  )
}

/** The game shell: named slots and modes; screens set the mode and fill slots. */
export function Shell() {
  return (
    <ShellProvider>
      <Layout><Outlet /></Layout>
    </ShellProvider>
  )
}
